import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { messageAPI, createMessageWS } from '@/services/api';
import { Send, MessageSquare, ArrowLeft, Wifi, WifiOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export default function MessagesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [typingUser, setTypingUser] = useState(null);
  const messagesEnd = useRef(null);
  const wsRef = useRef(null);
  const typingTimeout = useRef(null);
  const reconnectTimer = useRef(null);
	const activeConvRef = useRef(null);

  // Connect WebSocket
  const connectWS = useCallback(() => {
    if (!user) return;
    const token = localStorage.getItem('equimarket_token');
    if (!token) return;

    try {
      const ws = createMessageWS(token);

      ws.onopen = () => {
        setWsConnected(true);
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
				console.log(data);
        if (data.type === 'new_message') {
					// Only add to active chat if it belongs to the current conversation
					const currentConv = activeConvRef.current;
					if (currentConv && data.message.conversation_id === currentConv.conversation_id) {
						setMessages(prev => {
							if (prev.some(m => m.id === data.message.id)) return prev;
							return [...prev, data.message];
						});
						setTimeout(() => messagesEnd.current?.scrollIntoView({
							behavior: 'smooth',
							block: 'nearest', // or 'end'
							inline: 'nearest'
						}), 100);
					}
					// Always refresh conversation list (for unread badges, last message preview)
          loadConversations();
        } else if (data.type === 'typing') {
          setTypingUser(data.sender_id);
          clearTimeout(typingTimeout.current);
          typingTimeout.current = setTimeout(() => setTypingUser(null), 3000);
        }
      };

      ws.onclose = () => {
        setWsConnected(false);
        // Auto-reconnect after 3s
        reconnectTimer.current = setTimeout(connectWS, 3000);
      };

      ws.onerror = () => {
        ws.close();
      };

      wsRef.current = ws;
    } catch {
      setWsConnected(false);
    }
  }, [user]);

  useEffect(() => {
    loadConversations();
    connectWS();

    // Fallback polling for conversations list
    const interval = setInterval(loadConversations, 15000);

    return () => {
      clearInterval(interval);
      clearTimeout(reconnectTimer.current);
      clearTimeout(typingTimeout.current);
      if (wsRef.current) {
        wsRef.current.onclose = null; // Prevent reconnect on intentional close
        wsRef.current.close();
      }
    };
  }, [user, connectWS]);

  const loadConversations = async () => {
    try {
      const res = await messageAPI.conversations();
      setConversations(res.data || []);
    } catch {}
    setLoading(false);
  };

  const openConversation = async (conv) => {
    setActiveConv(conv);
		activeConvRef.current = conv;
    setTypingUser(null);
    try {
      const res = await messageAPI.getMessages(conv.conversation_id);
      setMessages(res.data || []);
			setTimeout(() => messagesEnd.current?.scrollIntoView({
				behavior: 'smooth',
				block: 'nearest', // or 'end'
				inline: 'nearest'
			}), 100);
      // Mark as read via WS
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'read', conversation_id: conv.conversation_id }));
      }
    } catch {}
  };

  // Fallback: poll for new messages in active conversation
  useEffect(() => {
    if (!activeConv || wsConnected) return;
    const interval = setInterval(async () => {
      try {
        const res = await messageAPI.getMessages(activeConv.conversation_id);
        setMessages(res.data || []);
      } catch {}
    }, 5000);
    return () => clearInterval(interval);
  }, [activeConv, wsConnected]);

  const sendTypingIndicator = () => {
    if (!activeConv || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({
      type: 'typing',
      recipient_id: activeConv.other_user?.id,
      conversation_id: activeConv.conversation_id
    }));
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMsg.trim() || !activeConv) return;
    setSending(true);
    try {
      await messageAPI.send({
        recipient_id: activeConv.other_user?.id,
        content: newMsg,
        ad_id: activeConv.last_message?.ad_id,
        ad_type: activeConv.last_message?.ad_type
      });
      setNewMsg('');
      // Fetch updated messages
      const res = await messageAPI.getMessages(activeConv.conversation_id);
      setMessages(res.data || []);
      loadConversations();
			setTimeout(() => messagesEnd.current?.scrollIntoView({
				behavior: 'smooth',
				block: 'nearest', // or 'end'
				inline: 'nearest'
			}), 100);
    } catch {}
    setSending(false);
  };

  if (!user) return null;

  return (
    <div className="pt-20 md:pt-24 min-h-screen" data-testid="messages-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-medium text-[#1A3C34] font-['Playfair_Display']">
						{t('messages.title')}
					</h1>
          <div className="flex items-center gap-2 text-xs" data-testid="ws-status">
            {wsConnected ? (
              <span className="flex items-center gap-1 text-green-700">
								<Wifi className="w-3.5 h-3.5" /> {t('messages.live')}
							</span>
            ) : (
              <span className="flex items-center gap-1 text-[#666055]">
								<WifiOff className="w-3.5 h-3.5" /> {t('messages.polling')}
							</span>
            )}
          </div>
        </div>

        <div className="bg-white border border-[#E6E2D6] rounded-sm overflow-hidden" style={{ height: 'calc(100vh - 220px)' }}>
          <div className="flex h-full">
            {/* Conversations List */}
            <div className={`w-full md:w-80 border-r border-[#E6E2D6] overflow-auto ${activeConv ? 'hidden md:block' : ''}`}
								 data-testid="conversations-list">
              {conversations.length === 0 ? (
                <div className="p-8 text-center">
                  <MessageSquare className="w-10 h-10 mx-auto mb-3 text-[#D4C5A9]" />
                  <p className="text-sm text-[#666055]">{t('messages.no_conversations')}</p>
                </div>
              ) : (
                conversations.map(conv => (
                  <button
                    key={conv.conversation_id}
                    onClick={() => openConversation(conv)}
                    className={`w-full p-4 flex items-start gap-3 hover:bg-[#F9F7F2] transition-colors text-left border-b border-[#E6E2D6] ${
                      activeConv?.conversation_id === conv.conversation_id ? 'bg-[#F2F0E9]' : ''
                    }`}
                    data-testid={`conv-${conv.conversation_id}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-[#1A3C34] flex items-center justify-center text-white text-sm font-semibold shrink-0">
                      {conv.other_user?.name?.charAt(0).toUpperCase() || '?'}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-sm text-[#1A3C34] truncate">
													{conv.other_user?.name || t('messages.user') }
												</p>
                        {conv.unread_count > 0 && (
                          <span className="bg-[#8B4513] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center shrink-0">
														{conv.unread_count}
													</span>
                        )}
                      </div>
                      <p className="text-xs text-[#666055] truncate mt-0.5">{conv.last_message?.content}</p>
                    </div>
                  </button>
                ))
              )}
            </div>

            {/* Messages Area */}
            <div className={`flex-1 flex flex-col ${!activeConv ? 'hidden md:flex' : 'flex'}`}>
              {activeConv ? (
                <>
                  {/* Header */}
                  <div className="p-4 border-b border-[#E6E2D6] flex items-center gap-3">
										<button onClick={() => { setActiveConv(null); activeConvRef.current = null; }}
														className="md:hidden"
														data-testid="back-to-list">
											<ArrowLeft className="w-5 h-5" />
										</button>
                    <div className="w-8 h-8 rounded-full bg-[#1A3C34] flex items-center justify-center text-white text-sm font-semibold">
                      {activeConv.other_user?.name?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold text-[#1A3C34]">{activeConv.other_user?.name}</p>
                      {typingUser === activeConv.other_user?.id && (
                        <p className="text-xs text-[#8B4513] animate-pulse" data-testid="typing-indicator">
													{t('messages.typing')}
												</p>
                      )}
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-auto p-4 space-y-3" data-testid="messages-list">
                    {messages.map(msg => (
                      <div key={msg.id} className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[70%] px-4 py-2.5 rounded-sm ${
                          msg.sender_id === user.id ? 'bg-[#1A3C34] text-white' : 'bg-[#F2F0E9] text-[#1A3C34]'
                        }`}>
                          <p className="text-sm">{msg.content}</p>
                          <p className={`text-[10px] mt-1 ${msg.sender_id === user.id ? 'text-white/50' : 'text-[#666055]/50'}`}>
                            {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEnd} />
                  </div>

                  {/* Input */}
                  <form onSubmit={sendMessage} className="p-4 border-t border-[#E6E2D6] flex gap-2"
												data-testid="message-form">
                    <Input
                      data-testid="message-input-field"
                      value={newMsg}
                      onChange={e => { setNewMsg(e.target.value); sendTypingIndicator(); }}
                      placeholder={t('messages.type_message')}
                      className="flex-1 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11"
                    />
                    <Button type="submit" disabled={sending || !newMsg.trim()}
														className="bg-[#1A3C34] hover:bg-[#153029] text-white rounded-sm h-11 px-4"
														data-testid="send-message-submit">
                      <Send className="w-4 h-4" />
                    </Button>
                  </form>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <MessageSquare className="w-12 h-12 mx-auto mb-4 text-[#D4C5A9]" />
                    <p className="text-[#666055]">{t('messages.select_conv')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
